import React, { useMemo } from 'react';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MDialog from '../../../components/controls/MDialog';
import MRadio from '../../../components/controls/MRadio';
import type { AbbrCheckResult, CityAbbreviationMatch, CityAbbreviationSelfMatch, SelfMatchChoice } from './types';
import { groupMatchesById } from './utils';

/** Props for {@link CheckAbbreviationDialog}. */
interface CheckAbbreviationDialogProps {
    abbrCheckResult: AbbrCheckResult | null;
    /** Per-row (keyed by `CityStateRow.id`) selected DB match; absent/`undefined` means "None". */
    matchSelections: Record<string, CityAbbreviationMatch>;
    onMatchSelectionChange: (rowId: string, choiceId: string, matchList: CityAbbreviationMatch[]) => void;
    onUpdateExisting: () => void;
    /** Per-pair (keyed by `${keepItemId}|${dropItemId}`) Keep/Uncheck/None choice for a self-match. */
    selfMatchChoice: Record<string, SelfMatchChoice>;
    onSelfMatchChoiceChange: (sm: CityAbbreviationSelfMatch, choice: SelfMatchChoice) => void;
    /** Per-pair editable merged CityAbbreviation preview. */
    selfMatchAbbreviations: Record<string, string>;
    onSelfMatchAbbreviationChange: (pairKey: string, value: string) => void;
    onApplySelectedChanges: () => void;
    /** Whether Step 1 (self-match resolution) has already been applied in the current dialog session. */
    step1Completed: boolean;
    /** Whether Step 2 (existing-record matching) has already been applied in the current dialog session. */
    step2Completed: boolean;
    /** Commits every completed step's staged changes and closes the dialog ("OK"). */
    onCommit: () => void;
    /** Discards every staged change made in the current session and closes the dialog ("Cancel"). */
    onCancel: () => void;
}

/**
 * Confirmation dialog for a **Check City Abbreviation** call. Shows up to two independent,
 * numbered workflow steps — each with its own action button placed directly beneath its
 * content so it's unambiguous which step a click applies to:
 *
 * - **Step 1 — Resolve duplicates within your selection** — rows detected as duplicates of
 *   *each other* (e.g. "Middleboro, MA" vs "Middleborough, MA"). Each pair defaults to **None**
 *   (left unchanged); the operator may instead pick which row to **Keep** (uncheck the other)
 *   and edit the merged CityAbbreviation preview, then click **Apply Selected Changes**.
 * - **Step 2 — Match against existing venue_city_states records** — rows matched against
 *   `venue_city_states`, grouped by the originating row. Every group defaults to **None** (left
 *   unchanged); picking a match redirects that row to update the matched record instead of
 *   inserting a new one, applied via **Update Existing City Abbreviation** — which, per the
 *   inline note shown next to it, only ever modifies that record's City Abbreviation field.
 *
 * Clicking a step's button **stages** that step's changes and keeps the dialog open — a green
 * check icon appears next to the step's heading and its controls (radios/text fields/button)
 * become disabled to prevent re-applying it. Nothing staged is written back to the grid until
 * the operator explicitly clicks the dialog's footer:
 *
 * - **OK** — commits every completed step's staged changes and closes the dialog.
 * - **Cancel** — discards all staged changes from the current session and closes the dialog.
 */
const CheckAbbreviationDialog: React.FC<CheckAbbreviationDialogProps> = ({
    abbrCheckResult, matchSelections, onMatchSelectionChange, onUpdateExisting,
    selfMatchChoice, onSelfMatchChoiceChange, selfMatchAbbreviations, onSelfMatchAbbreviationChange,
    onApplySelectedChanges, step1Completed, step2Completed, onCommit, onCancel,
}) => {
    const groupedMatches = useMemo<Record<string, CityAbbreviationMatch[]>>(
        () => groupMatchesById(abbrCheckResult?.matches || []),
        [abbrCheckResult]
    );

    const hasSteps = !!abbrCheckResult?.selfMatches.length || !!abbrCheckResult?.matches.length;

    return (
        <MDialog
            open={!!abbrCheckResult}
            title="Check City Abbreviation"
            handleClose={onCancel}
            closeLabel="Cancel"
            maxWidth="md"
            action={
                <Button onClick={onCommit} variant="contained" color="primary" disabled={hasSteps && !(step1Completed || step2Completed)}>
                    OK
                </Button>
            }
        >
            {abbrCheckResult?.matches.length === 0 && abbrCheckResult?.selfMatches.length === 0 && (
                <Typography>No matching records found in venue_city_states — continue with Insert.</Typography>
            )}
            {hasSteps && (
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                    Complete the step(s) below — each is staged locally and marked with a{' '}
                    <CheckCircleIcon fontSize="inherit" color="success" sx={{ verticalAlign: 'text-bottom' }} /> once
                    applied. Nothing is saved until you click <b>OK</b>; click <b>Cancel</b> at any time to discard
                    every change made in this session.
                </Typography>
            )}
            {!!abbrCheckResult?.selfMatches.length && (
                <Box sx={{ mb: 3, p: 2, border: step1Completed ? '1px solid #2e7d32' : '1px solid rgb(48, 46, 44)', borderRadius: 1, bgcolor: step1Completed ? 'success.50' : undefined }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Step — Resolve {abbrCheckResult.selfMatches.length} duplicate row(s) within your selection
                        </Typography>
                        {step1Completed && <CheckCircleIcon color="success" fontSize="small" titleAccess="Step 1 applied" />}
                    </Stack>
                    <Typography sx={{ mb: 1.5 }}>
                        {step1Completed
                            ? 'Applied for this session — click OK below to save, or Cancel to discard.'
                            : (
                                <>
                                    Each pair below likely refers to the same city typed differently. Every pair defaults to
                                    <b> None</b> (unchanged) — pick which row to keep (and which to uncheck), review/edit the
                                    merged City Abbreviation, then click <b>Apply Selected Changes</b> below.
                                </>
                            )}
                    </Typography>
                    {abbrCheckResult.selfMatches.map((sm) => {
                        const pairKey = `${sm.keepItemId}|${sm.dropItemId}`;
                        const choice = selfMatchChoice[pairKey] || 'none';
                        const keepLabel = choice === 'drop' ? sm.dropCity : sm.keepCity;
                        return (
                            <Box key={pairKey} sx={{ mb: 2, p: 1.5, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                                <MRadio
                                    label={`Duplicate pair (${sm.state})`}
                                    choices={[
                                        { id: 'none', name: 'None (leave unchanged)' },
                                        { id: 'keep', name: `Keep ${sm.keepCity}, uncheck ${sm.dropCity}` },
                                        { id: 'drop', name: `Keep ${sm.dropCity}, uncheck ${sm.keepCity}` },
                                    ]}
                                    defaultValue="none"
                                    isDisplayRow={false}
                                    disabled={step1Completed}
                                    onChange={(id: string) => onSelfMatchChoiceChange(sm, id as SelfMatchChoice)}
                                />
                                <TextField
                                    label={`City Abbreviation for ${keepLabel}`}
                                    size="small"
                                    fullWidth
                                    disabled={choice === 'none' || step1Completed}
                                    value={selfMatchAbbreviations[pairKey] ?? ''}
                                    onChange={(e) => onSelfMatchAbbreviationChange(pairKey, e.target.value)}
                                    sx={{ mt: 1 }}
                                />
                            </Box>
                        );
                    })}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            onClick={onApplySelectedChanges}
                            variant="outlined"
                            color="primary"
                            disabled={step1Completed}
                            startIcon={step1Completed ? <CheckCircleIcon color="success" /> : undefined}
                        >
                            {step1Completed ? 'Applied' : 'Apply Selected Changes'}
                        </Button>
                    </Box>
                </Box>
            )}
            {!!abbrCheckResult?.matches.length && (
                <Box sx={{ p: 2, border: step2Completed ? '1px solid #2e7d32' : '1px solid #e0e0e0', borderRadius: 1, bgcolor: step2Completed ? 'success.50' : undefined }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Step — Match against existing venue_city_states records
                        </Typography>
                        {step2Completed && <CheckCircleIcon color="success" fontSize="small" titleAccess="Step 2 applied" />}
                    </Stack>
                    <Typography sx={{ mb: 2 }}>
                        {step2Completed
                            ? 'Applied for this session — click OK below to save, or Cancel to discard.'
                            : (
                                <>
                                    Found {abbrCheckResult.matches.length} matching record(s) across {Object.keys(groupedMatches).length} row(s)
                                    in venue_city_states. Every row defaults to <b>None</b> (unchanged) — pick a match below to
                                    redirect that row to update the existing record instead of inserting a new one, then click
                                    <b> Update Existing City Abbreviation</b>.
                                </>
                            )}
                    </Typography>
                    {Object.entries(groupedMatches).map(([rowId, matchList]) => {
                        const sourceRow = abbrCheckResult.selectedRows.find((r) => r.id === rowId);
                        return (
                            <Box key={rowId} sx={{ mb: 2, p: 1.5, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                    {sourceRow?.city || rowId} ({sourceRow?.stateId || 'N/A'}) — {matchList.length} match(es)
                                </Typography>
                                <MRadio
                                    label={`Match for ${sourceRow?.city || rowId}`}
                                    choices={[
                                        { id: 'none', name: 'None (leave unchanged)' },
                                        ...matchList.map((m, i) => ({
                                            id: String(i),
                                            name: `${m.city}, ${m.state} (${m.country || 'N/A'}) — Abbreviation: ${m.cityAbbreviation || '(none)'}`,
                                        })),
                                    ]}
                                    defaultValue="none"
                                    isDisplayRow={false}
                                    disabled={step2Completed}
                                    onChange={(idx: string) => onMatchSelectionChange(rowId, idx, matchList)}
                                />
                            </Box>
                        );
                    })}
                    <Typography color="text.secondary" sx={{ mb: 1.5 }}>
                        Insert all rows as new records, or update the selected matches’ City Abbreviation instead?
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                        Note: this only updates the matched record’s City Abbreviation field — no other field is modified.
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            onClick={onUpdateExisting}
                            variant="contained"
                            color="primary"
                            disabled={step2Completed || Object.keys(matchSelections).length === 0}
                            startIcon={step2Completed ? <CheckCircleIcon color="success" /> : undefined}
                        >
                            {step2Completed ? 'Applied' : 'Update Existing City Abbreviation'}
                        </Button>
                    </Box>
                </Box>
            )}
        </MDialog>
    );
};

export default CheckAbbreviationDialog;
